import {
  Box,
  Card,
  CardBody,
  Heading,
  Text,
  VStack,
  HStack,
  Icon,
  Flex,
  Spinner,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Input,
  FormControl,
  FormLabel,
  useToast,
} from "@chakra-ui/react";
import { FaHourglassHalf, FaCheckCircle, FaExclamationCircle, FaUserClock } from "react-icons/fa";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getBatchQueueSummary, processBatchQueue } from "@/services/consignado/batch-queue.service";
import { useState } from "react";

interface StatusData {
  name: string;
  value: number;
  color: string;
  icon: any;
  hexColor: string;
}

export default function ConsignadoProcessingQueueSummary() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [processLimit, setProcessLimit] = useState<string>("");

  const { data: summary, isLoading, isError, refetch } = useQuery({
    queryKey: ["batchQueueSummary"],
    queryFn: () => getBatchQueueSummary(),
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  });

  const { mutate: handleProcessBatch, isPending: isProcessing } = useMutation({
    mutationFn: async () => {
      const limit = processLimit ? parseInt(processLimit) : undefined;
      return await processBatchQueue(limit);
    },
    onSuccess: () => {
      toast({
        title: "Sucesso",
        description: "Processamento da fila iniciado com sucesso",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setProcessLimit("");
      onClose();
      // Refetch o resumo após processar
      setTimeout(() => {
        refetch();
      }, 1000);
    },
    onError: (error: any) => {
      toast({
        title: "Erro",
        description: error?.response?.data?.message || "Erro ao processar fila",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const summaryData: StatusData[] = [
    {
      name: "Em Processamento",
      value: summary?.processing ?? 0,
      icon: FaHourglassHalf,
      color: "yellow.400",
      hexColor: "#ECC94B",
    },
    {
      name: "Aguardando Cliente",
      value: summary?.pending ?? 0,
      icon: FaUserClock,
      color: "blue.400",
      hexColor: "#4299E1",
    },
    {
      name: "Concluído",
      value: summary?.success ?? 0,
      icon: FaCheckCircle,
      color: "green.400",
      hexColor: "#48BB78",
    },
    {
      name: "Erros",
      value: summary?.failed ?? 0,
      icon: FaExclamationCircle,
      color: "red.400",
      hexColor: "#F56565",
    },
  ];

  const chartData = summaryData.map((item) => ({
    name: item.name,
    value: item.value,
  }));

  const total = summary?.totalQueued ?? 0;

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          bg="white"
          p={3}
          borderRadius="md"
          boxShadow="lg"
          border="1px solid"
          borderColor="gray.200"
        >
          <Text fontWeight="bold" fontSize="sm">
            {payload[0].name}
          </Text>
          <Text fontSize="sm" color="gray.600">
            {payload[0].value} itens
          </Text>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box w="full">
      <Card shadow="md" borderRadius="md" w="full">
        <CardBody>
          <VStack spacing={6} align="stretch">
            <HStack justify="space-between" align="center">
              <Heading size="md" color="gray.500" fontFamily="Lexend Deca">
                Resumo da Fila de Processamento
              </Heading>
              <Button
                colorScheme="blue"
                size="sm"
                onClick={onOpen}
                isDisabled={isLoading || (summary?.pending ?? 0) === 0}
                fontFamily="Lexend Deca"
              >
                Processar Fila
              </Button>
            </HStack>

            {isLoading && (
              <Flex justify="center" align="center" minH="300px">
                <Spinner size="lg" color="blue.400" thickness="4px" />
              </Flex>
            )}

            {isError && (
              <Box
                bg="red.50"
                p={4}
                borderRadius="md"
                border="1px solid"
                borderColor="red.200"
              >
                <Text color="red.700" fontFamily="Lexend Deca" fontWeight="600">
                  Erro ao carregar resumo da fila de processamento
                </Text>
              </Box>
            )}

            {!isLoading && !isError && (
              <Flex
                direction={{ base: "column", lg: "row" }}
                gap={8}
                align="center"
                justify="space-between"
              >
                {/* Gráfico de Pizza */}
                <Box w={{ base: "full", lg: "45%" }} h="300px">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {summaryData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.hexColor} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        formatter={(value, entry) => {
                          const data = entry.payload as any;
                          return `${value}: ${data.value}`;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>

                {/* Cards de Status */}
                <VStack spacing={3} w={{ base: "full", lg: "45%" }} align="stretch">
                  <Box
                    bg="gray.50"
                    p={4}
                    borderRadius="md"
                    borderLeft="4px"
                    borderColor="blue.400"
                  >
                    <HStack justify="space-between" mb={1}>
                      <HStack spacing={2}>
                        <Icon as={FaHourglassHalf} color="yellow.400" />
                        <Text fontWeight="600" fontFamily="Lexend Deca" color="gray.700">
                          Em Processamento
                        </Text>
                      </HStack>
                      <Text fontSize="2xl" fontWeight="bold" color="yellow.400">
                        {summaryData[0].value}
                      </Text>
                    </HStack>
                    <Text fontSize="xs" color="gray.500" fontFamily="Lexend Deca">
                      {total > 0 ? ((summaryData[0].value / total) * 100).toFixed(1) : 0}% do total
                    </Text>
                  </Box>

                  <Box
                    bg="gray.50"
                    p={4}
                    borderRadius="md"
                    borderLeft="4px"
                    borderColor="blue.400"
                  >
                    <HStack justify="space-between" mb={1}>
                      <HStack spacing={2}>
                        <Icon as={FaUserClock} color="blue.400" />
                        <Text fontWeight="600" fontFamily="Lexend Deca" color="gray.700">
                          Aguardando Cliente
                        </Text>
                      </HStack>
                      <Text fontSize="2xl" fontWeight="bold" color="blue.400">
                        {summaryData[1].value}
                      </Text>
                    </HStack>
                    <Text fontSize="xs" color="gray.500" fontFamily="Lexend Deca">
                      {total > 0 ? ((summaryData[1].value / total) * 100).toFixed(1) : 0}% do total
                    </Text>
                  </Box>

                  <Box
                    bg="gray.50"
                    p={4}
                    borderRadius="md"
                    borderLeft="4px"
                    borderColor="green.400"
                  >
                    <HStack justify="space-between" mb={1}>
                      <HStack spacing={2}>
                        <Icon as={FaCheckCircle} color="green.400" />
                        <Text fontWeight="600" fontFamily="Lexend Deca" color="gray.700">
                          Concluído
                        </Text>
                      </HStack>
                      <Text fontSize="2xl" fontWeight="bold" color="green.400">
                        {summaryData[2].value}
                      </Text>
                    </HStack>
                    <Text fontSize="xs" color="gray.500" fontFamily="Lexend Deca">
                      {total > 0 ? ((summaryData[2].value / total) * 100).toFixed(1) : 0}% do total
                    </Text>
                  </Box>

                  <Box
                    bg="gray.50"
                    p={4}
                    borderRadius="md"
                    borderLeft="4px"
                    borderColor="red.400"
                  >
                    <HStack justify="space-between" mb={1}>
                      <HStack spacing={2}>
                        <Icon as={FaExclamationCircle} color="red.400" />
                        <Text fontWeight="600" fontFamily="Lexend Deca" color="gray.700">
                          Erros
                        </Text>
                      </HStack>
                      <Text fontSize="2xl" fontWeight="bold" color="red.400">
                        {summaryData[3].value}
                      </Text>
                    </HStack>
                    <Text fontSize="xs" color="gray.500" fontFamily="Lexend Deca">
                      {total > 0 ? ((summaryData[3].value / total) * 100).toFixed(1) : 0}% do total
                    </Text>
                  </Box>

                  {/* Total */}
                  <Box
                    bg="gradient-to-r"
                    p={4}
                    borderRadius="md"
                    border="2px solid"
                    borderColor="gray.200"
                    mt={2}
                  >
                    <HStack justify="space-between">
                      <Text fontWeight="600" fontFamily="Lexend Deca" color="gray.700">
                        Total na Fila
                      </Text>
                      <Text fontSize="2xl" fontWeight="bold" color="gray.900">
                        {total}
                      </Text>
                    </HStack>
                  </Box>
                </VStack>
              </Flex>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Modal para definir limite de processamento */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontFamily="Lexend Deca">Processar Fila de Batch</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Text color="gray.600" fontFamily="Lexend Deca">
                Quantos itens deseja processar nesta execução?
              </Text>
              <FormControl>
                <FormLabel fontFamily="Lexend Deca" fontSize="sm">
                  Limite de itens (deixe em branco para processar todos)
                </FormLabel>
                <Input
                  type="number"
                  placeholder="Ex: 100"
                  value={processLimit}
                  onChange={(e) => setProcessLimit(e.target.value)}
                  min="1"
                  fontFamily="Lexend Deca"
                />
              </FormControl>
              <Text fontSize="xs" color="gray.500" fontFamily="Lexend Deca">
                Itens aguardando: {summary?.pending ?? 0}
              </Text>
            </VStack>
          </ModalBody>

          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={onClose} fontFamily="Lexend Deca">
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => handleProcessBatch()}
              isLoading={isProcessing}
              loadingText="Processando..."
              fontFamily="Lexend Deca"
            >
              Processar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
